/**
 * OVERVOLT §18 juice layer — render-/audio-only game-feel effects.
 *
 * Everything in this module is strictly a **render/audio overlay**: it reads
 * the simulation state but never writes it, never enters the `state` snapshot,
 * and draws every bit of jitter from a **dedicated render-only RNG** seeded off
 * the wall clock — never the sim's seeded `mulberry32` stream. The whole layer
 * is gated by `CONFIG.juiceEnabled`; with it off (stable-pixel automated runs)
 * none of this runs. The deterministic `state.particles` kill burst is left
 * untouched — this is a *separate* layer composited on top. So no seed and no
 * QA golden value shifts. See docs/GAME_DESIGN.md §18.
 *
 * Hit-stop lives here too: {@link trackJuice} arms a wall-clock freeze window
 * on a Kernel phase transition / death, and {@link juiceHitStopActive} is
 * polled by the rAF→tick scheduler in `Game.fixedUpdate`. The `tick(n)` test
 * hook bypasses that scheduler entirely, so hit-stop is invisible to tests and
 * to determinism.
 */
import { CONFIG } from '../config';
import type { Audio } from '../engine/audio';
import { clamp, mulberry32, withAlpha } from '../engine/util';
import { enemyColor, type Enemy } from './entities';
import type { GameState } from './state';

/** A 2D screen-space point. */
interface Pt {
  x: number;
  y: number;
}

const C = CONFIG.colors;
const FONT_NUM = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';

// --- render-only RNG ---------------------------------------------------------

/**
 * A dedicated render-only PRNG, seeded off the wall clock at module load. It is
 * deliberately *separate* from the sim's seeded stream — every VFX jitter draws
 * from here, so juice never perturbs determinism (docs §18).
 */
let rngState = ((Date.now() & 0xffffffff) ^ Math.floor(performance.now() * 1000)) >>> 0;

/** Next render-only pseudo-random float in [0, 1). */
function rnd(): number {
  rngState = (rngState + 0x6d2b79f5) >>> 0;
  return mulberry32(rngState);
}

// --- effect records ----------------------------------------------------------

/** A rising, fading floating damage / heal numeral. */
interface DamageNumber {
  x: number;
  y: number;
  /** Horizontal drift over the rise, px. */
  drift: number;
  text: string;
  color: string;
  /** Wall-clock birth time, seconds. */
  born: number;
  /** Larger font for a heavy hit (Sniper / Mortar / Airstrike / any Kernel hit). */
  big: boolean;
}

/** A short tower-coloured spark flash at a shot's impact point. */
interface Spark {
  x: number;
  y: number;
  color: string;
  born: number;
  /** Per-spark spoke rotation, radians. */
  rot: number;
}

/** A brief enemy-coloured kill flash — an expanding ring layered over the sim burst. */
interface KillFlash {
  x: number;
  y: number;
  color: string;
  born: number;
}

/** A white shock flash marking a Kernel phase transition. */
interface PhaseFlash {
  x: number;
  y: number;
  born: number;
}

/** One ember of the Kernel's multi-stage death bloom. */
interface Ember {
  /** Launch angle, radians. */
  a: number;
  /** Launch speed, px/s. */
  spd: number;
  /** Ember radius, px. */
  r: number;
  color: string;
}

/** The Kernel's multi-stage death explosion — flash, shock ring, particle bloom. */
interface KernelDeath {
  x: number;
  y: number;
  born: number;
  embers: Ember[];
}

/** One decaying screen-shake impulse. */
interface ShakeImpulse {
  born: number;
  /** Peak offset, px. */
  mag: number;
}

// --- effect lifetimes (seconds) ----------------------------------------------

const SPARK_LIFE = 0.17;
const KILL_FLASH_LIFE = 0.34;
const PHASE_FLASH_LIFE = 0.55;
const KERNEL_DEATH_LIFE = 1.6;
const SHAKE_LIFE = 0.36;

// --- module state ------------------------------------------------------------

let damageNumbers: DamageNumber[] = [];
let sparks: Spark[] = [];
let killFlashes: KillFlash[] = [];
let phaseFlashes: PhaseFlash[] = [];
let kernelDeaths: KernelDeath[] = [];
let shakes: ShakeImpulse[] = [];
/** Wall-clock ms until which the rAF→tick scheduler withholds simulation ticks. */
let hitStopUntilMs = 0;

/** Per-enemy snapshot from the previous tracked frame — drives the state diff. */
interface EnemyTrack {
  x: number;
  y: number;
  hp: number;
  shield: number;
  dist: number;
  phase: number;
  type: Enemy['type'];
}
let prevEnemy = new Map<number, EnemyTrack>();
let prevLives = -1;
let prevDrainTicks = 0;
let prevDrainCharge = 0;
let prevTick = -1;
let prevNextId = 0;
let maxProjId = 0;

/** Drop every live effect + diff tracker — called on a detected `reset()`. */
function resetJuice(): void {
  damageNumbers = [];
  sparks = [];
  killFlashes = [];
  phaseFlashes = [];
  kernelDeaths = [];
  shakes = [];
  hitStopUntilMs = 0;
  prevEnemy = new Map();
  prevLives = -1;
  prevDrainTicks = 0;
  prevDrainCharge = 0;
  maxProjId = 0;
}

// --- public scheduler queries ------------------------------------------------

/**
 * True while a Kernel-beat hit-stop window is open. Polled by the rAF→tick
 * scheduler (`Game.fixedUpdate`) to briefly withhold tick advancement — see
 * docs §18.1.3. The `tick(n)` test hook never consults this, so determinism is
 * untouched.
 */
export function juiceHitStopActive(): boolean {
  return performance.now() < hitStopUntilMs;
}

/**
 * The current screen-shake offset (px) — the summed, decaying contribution of
 * every live impulse, jittered by the render-only RNG. Applied as a translate
 * around the *playfield* only; the HUD strip is never shaken (readability).
 */
export function juiceShakeOffset(time: number): Pt {
  let mag = 0;
  for (const s of shakes) {
    const k = 1 - (time - s.born) / SHAKE_LIFE;
    if (k > 0) mag += s.mag * k * k;
  }
  if (mag <= 0) return { x: 0, y: 0 };
  const a = rnd() * Math.PI * 2;
  return { x: Math.cos(a) * mag, y: Math.sin(a) * mag };
}

// --- effect spawners ---------------------------------------------------------

/** Arm a hit-stop window of `ms`, never shortening an already-longer one. */
function armHitStop(ms: number): void {
  hitStopUntilMs = Math.max(hitStopUntilMs, performance.now() + ms);
}

/** Queue a decaying screen-shake impulse of peak `mag` px. */
function addShake(mag: number, time: number): void {
  shakes.push({ born: time, mag });
}

/** Push a floating damage / heal numeral, evicting the oldest past the pool cap. */
function pushDamageNumber(
  x: number,
  y: number,
  text: string,
  color: string,
  big: boolean,
  time: number,
): void {
  damageNumbers.push({ x, y, drift: (rnd() - 0.5) * 16, text, color, born: time, big });
  while (damageNumbers.length > CONFIG.damageNumberPoolMax) damageNumbers.shift();
}

// --- the per-frame state diff ------------------------------------------------

/**
 * Diff the run against the previous tracked frame and spawn juice VFX, screen
 * shake, hit-stop windows and boss SFX. Called once per render frame from the
 * renderer (only while `CONFIG.juiceEnabled`). Reads state; never mutates it.
 *
 * @param enemyPos live screen position of every alive enemy this frame.
 * @param audio    audio sink for the §18 boss SFX (respects the mute toggle).
 */
export function trackJuice(
  state: GameState,
  enemyPos: Map<number, Pt>,
  time: number,
  audio: Audio,
): void {
  // A reset() drops the tick / id counters — clear stale effects + trackers so
  // nothing lingers across runs (mirrors the renderer's other diff trackers).
  if (state.tick < prevTick || state.tick === 0 || state.nextId < prevNextId) {
    resetJuice();
  }
  prevTick = state.tick;
  prevNextId = state.nextId;

  // Floating damage numbers + kill flashes + Kernel beats — one enemy diff pass.
  const seen = new Map<number, EnemyTrack>();
  for (const e of state.enemies) {
    const pos = enemyPos.get(e.id);
    if (!pos) continue;
    const track: EnemyTrack = {
      x: pos.x,
      y: pos.y,
      hp: e.hp,
      shield: e.shield,
      dist: e.distToCore,
      phase: e.phase,
      type: e.type,
    };
    seen.set(e.id, track);
    const prev = prevEnemy.get(e.id);
    if (prev) {
      // Shield-absorbed damage — a cyan numeral above the body.
      const shieldHit = prev.shield - e.shield;
      if (shieldHit > 0.5) {
        pushDamageNumber(pos.x, pos.y - 7, String(Math.round(shieldHit)), '#2de2e6', false, time);
      }
      const hpDelta = prev.hp - e.hp;
      if (hpDelta > 0.5) {
        // White HP damage — a larger font for a heavy hit / any Kernel hit.
        const big = e.type === 'kernel' || hpDelta >= 16;
        pushDamageNumber(pos.x, pos.y, String(Math.round(hpDelta)), '#ffffff', big, time);
      } else if (hpDelta < -0.5) {
        // A Patch repair — an acid-green `+N`.
        pushDamageNumber(pos.x, pos.y, `+${Math.round(-hpDelta)}`, C.corrode, false, time);
      }
      // A Kernel phase transition — a big beat: shake, hit-stop, sting, flash.
      if (e.type === 'kernel' && e.phase > prev.phase) {
        addShake(CONFIG.shakeBig, time);
        armHitStop(CONFIG.hitStopBossPhaseMs);
        phaseFlashes.push({ x: pos.x, y: pos.y, born: time });
        audio.play('boss-phase');
      }
    } else if (e.type === 'kernel') {
      // A fresh Kernel — the spawn roar + alarm sting (the banner is render.ts).
      audio.play('boss-incoming');
      audio.play('boss-spawn');
    }
    prevEnemy.set(e.id, track);
  }

  // Departed enemies: a Kernel that vanished mid-field died → death sequence;
  // a routine Glitch killed (not leaked) → an enriched kill flash.
  for (const [id, prev] of prevEnemy) {
    if (seen.has(id)) continue;
    if (prev.type === 'kernel') {
      // A killed boss vanishes mid-field; a leaked one vanishes at the Core
      // (`dist ≈ 0`) — only a kill earns the climactic explosion.
      if (prev.dist > 1.2) {
        kernelDeaths.push({ x: prev.x, y: prev.y, born: time, embers: makeEmbers() });
        addShake(CONFIG.shakeBig, time);
        armHitStop(CONFIG.hitStopBossDeathMs);
        audio.play('boss-death');
      }
    } else if (prev.dist > 0.8) {
      killFlashes.push({ x: prev.x, y: prev.y, color: enemyColor(prev.type), born: time });
    }
  }
  prevEnemy = seen;

  // Per-shot spark flashes — one at every fresh beam's impact point.
  let nextProj = maxProjId;
  for (const p of state.projectiles) {
    if (p.id > maxProjId && p.kind !== 'airstrike') {
      // Projectile coords are playfield-local; lift into canvas space.
      sparks.push({
        x: p.to.x,
        y: p.to.y + CONFIG.hudHeight,
        color: CONFIG.towers[p.kind].color,
        born: time,
        rot: rnd() * Math.PI,
      });
    }
    if (p.id > nextProj) nextProj = p.id;
  }
  // A new Mortar / Airstrike blast also shakes the field (small / medium).
  for (const p of state.projectiles) {
    if (p.id <= maxProjId) continue;
    if (p.kind === 'mortar') addShake(CONFIG.shakeSmall, time);
    else if (p.kind === 'airstrike') addShake(CONFIG.shakeMedium, time);
  }
  maxProjId = nextProj;

  // A Power Drain emit — the grid lurches: a medium shake + a sub-bass thud.
  if (state.drainTicksLeft > prevDrainTicks) {
    addShake(CONFIG.shakeMedium, time);
    audio.play('boss-drain-emit');
  }
  prevDrainTicks = state.drainTicksLeft;
  // A Power Drain charge beginning — the rising telegraph whine.
  if (prevDrainCharge === 0 && state.drainCharge > 0) {
    audio.play('boss-drain-charge');
  }
  prevDrainCharge = state.drainCharge;

  // A life lost — a medium shake (the life-lost SFX is the sim's own cue).
  if (prevLives >= 0 && state.lives < prevLives) addShake(CONFIG.shakeMedium, time);
  prevLives = state.lives;

  // Age out finished effects.
  damageNumbers = damageNumbers.filter((d) => time - d.born < CONFIG.damageNumberRiseMs / 1000);
  sparks = sparks.filter((s) => time - s.born < SPARK_LIFE);
  killFlashes = killFlashes.filter((k) => time - k.born < KILL_FLASH_LIFE);
  phaseFlashes = phaseFlashes.filter((p) => time - p.born < PHASE_FLASH_LIFE);
  kernelDeaths = kernelDeaths.filter((k) => time - k.born < KERNEL_DEATH_LIFE);
  shakes = shakes.filter((s) => time - s.born < SHAKE_LIFE);
}

/** Build the Kernel death bloom's ember field from the render-only RNG. */
function makeEmbers(): Ember[] {
  const embers: Ember[] = [];
  for (let i = 0; i < 26; i += 1) {
    embers.push({
      a: rnd() * Math.PI * 2,
      spd: 70 + rnd() * 210,
      r: 1.6 + rnd() * 3.4,
      color: rnd() < 0.5 ? '#ffe8ec' : rnd() < 0.6 ? '#ff2d55' : '#ff8c42',
    });
  }
  return embers;
}

// --- drawing -----------------------------------------------------------------

/**
 * Draw the world-space juice layer — sparks, kill flashes, the Kernel death
 * explosion, phase-transition flashes and floating damage numbers. Called
 * inside the shaken playfield transform so these effects ride the screen
 * shake. Boss UI (HP bar, banner) is drawn un-shaken by the renderer instead.
 */
export function drawJuiceWorld(ctx: CanvasRenderingContext2D, time: number): void {
  drawSparks(ctx, time);
  drawKillFlashes(ctx, time);
  for (const k of kernelDeaths) drawKernelDeath(ctx, k, time);
  for (const p of phaseFlashes) drawPhaseFlash(ctx, p, time);
  drawDamageNumbers(ctx, time);
}

/** A soft additive radial bloom — juice's own glow (no shared sprite cache). */
function bloom(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, alpha: number): void {
  if (alpha <= 0 || r <= 0) return;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, withAlpha(color, clamp(alpha, 0, 1)));
  g.addColorStop(0.45, withAlpha(color, clamp(alpha, 0, 1) * 0.4));
  g.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

/** Tower-coloured impact sparks — a tiny radial burst of bright spokes. */
function drawSparks(ctx: CanvasRenderingContext2D, time: number): void {
  if (sparks.length === 0) return;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';
  for (const s of sparks) {
    const p = clamp((time - s.born) / SPARK_LIFE, 0, 1);
    const fade = 1 - p;
    bloom(ctx, s.x, s.y, 9 * fade + 3, s.color, 0.6 * fade);
    ctx.strokeStyle = withAlpha('#ffffff', 0.9 * fade);
    ctx.lineWidth = 1.5 * fade + 0.4;
    const len = 4 + p * 9;
    for (let i = 0; i < 5; i += 1) {
      const a = s.rot + (Math.PI * 2 * i) / 5;
      ctx.beginPath();
      ctx.moveTo(s.x + Math.cos(a) * 2, s.y + Math.sin(a) * 2);
      ctx.lineTo(s.x + Math.cos(a) * len, s.y + Math.sin(a) * len);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Enemy-coloured kill flashes — an expanding ring layered over the sim burst. */
function drawKillFlashes(ctx: CanvasRenderingContext2D, time: number): void {
  if (killFlashes.length === 0) return;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const k of killFlashes) {
    const p = clamp((time - k.born) / KILL_FLASH_LIFE, 0, 1);
    const fade = 1 - p;
    const rr = 6 + p * 22;
    bloom(ctx, k.x, k.y, 14 * fade + 4, k.color, 0.5 * fade);
    ctx.strokeStyle = withAlpha(k.color, 0.7 * fade);
    ctx.lineWidth = 2.4 * fade + 0.4;
    ctx.beginPath();
    ctx.arc(k.x, k.y, rr, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = withAlpha('#ffffff', 0.5 * fade);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(k.x, k.y, rr * 0.62, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** A white shock flash + ring marking a Kernel phase transition. */
function drawPhaseFlash(ctx: CanvasRenderingContext2D, f: PhaseFlash, time: number): void {
  const p = clamp((time - f.born) / PHASE_FLASH_LIFE, 0, 1);
  const fade = 1 - p;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  bloom(ctx, f.x, f.y, 64 * (0.4 + p) , '#ffe8ec', 0.7 * fade);
  ctx.strokeStyle = withAlpha('#ffffff', 0.85 * fade);
  ctx.lineWidth = 4 * fade + 0.6;
  ctx.beginPath();
  ctx.arc(f.x, f.y, 12 + p * 78, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = withAlpha(C.kernel, 0.6 * fade);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(f.x, f.y, 12 + p * 104, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * The Kernel's multi-stage death explosion — a white flash, an expanding twin
 * shock ring, then a long drifting ember bloom: the campaign's visual climax
 * (docs §18.4). Paired with the big shake + hit-stop armed in {@link trackJuice}.
 */
function drawKernelDeath(ctx: CanvasRenderingContext2D, k: KernelDeath, time: number): void {
  const dt = time - k.born;
  const p = clamp(dt / KERNEL_DEATH_LIFE, 0, 1);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  // Stage 1 — a hard white flash that snaps out fast.
  if (dt < 0.26) {
    const fp = 1 - dt / 0.26;
    bloom(ctx, k.x, k.y, 150 * (0.5 + 0.5 * (1 - fp)), '#ffffff', 0.95 * fp);
  }
  // Stage 2 — an expanding twin shock ring.
  if (dt < 0.85) {
    const rp = clamp(dt / 0.85, 0, 1);
    const fade = 1 - rp;
    const rr = 18 + rp * 190;
    ctx.strokeStyle = withAlpha('#ffe8ec', 0.85 * fade);
    ctx.lineWidth = 7 * fade + 1;
    ctx.beginPath();
    ctx.arc(k.x, k.y, rr, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = withAlpha(C.kernel, 0.7 * fade);
    ctx.lineWidth = 3.5 * fade + 0.6;
    ctx.beginPath();
    ctx.arc(k.x, k.y, rr * 0.7, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Stage 3 — a long ember bloom drifting + decelerating outward.
  const ep = clamp(dt, 0, KERNEL_DEATH_LIFE);
  const eFade = 1 - p;
  // Eased travel: fast then settling.
  const travel = 1 - Math.pow(1 - clamp(dt / 1.1, 0, 1), 2);
  for (const em of k.embers) {
    const reach = em.spd * 1.1 * travel;
    const ex = k.x + Math.cos(em.a) * reach;
    const ey = k.y + Math.sin(em.a) * reach + ep * ep * 26; // a little gravity
    const er = em.r * eFade;
    bloom(ctx, ex, ey, er * 3.4, em.color, 0.75 * eFade);
    ctx.fillStyle = withAlpha(em.color, 0.9 * eFade);
    ctx.beginPath();
    ctx.arc(ex, ey, Math.max(0.4, er), 0, Math.PI * 2);
    ctx.fill();
  }
  // A lingering smoulder core.
  bloom(ctx, k.x, k.y, 40 * eFade + 6, C.kernel, 0.5 * eFade);
  ctx.restore();
}

/** Rising, fading floating damage / heal numerals. */
function drawDamageNumbers(ctx: CanvasRenderingContext2D, time: number): void {
  if (damageNumbers.length === 0) return;
  const life = CONFIG.damageNumberRiseMs / 1000;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const d of damageNumbers) {
    const p = clamp((time - d.born) / life, 0, 1);
    // Rise ≈22 px; ease the fade so numbers stay legible most of their life.
    const alpha = p < 0.55 ? 1 : 1 - (p - 0.55) / 0.45;
    const y = d.y - 9 - p * 22;
    const x = d.x + d.drift * p;
    const size = d.big ? 17 : 11;
    ctx.font = `700 ${size}px ${FONT_NUM}`;
    ctx.fillStyle = withAlpha('#05070f', clamp(alpha, 0, 1) * 0.85);
    ctx.fillText(d.text, x + 1, y + 1.4);
    ctx.save();
    ctx.shadowBlur = d.big ? 8 : 5;
    ctx.shadowColor = d.color;
    ctx.fillStyle = withAlpha(d.color, clamp(alpha, 0, 1));
    ctx.fillText(d.text, x, y);
    ctx.restore();
  }
  ctx.restore();
}
